// Turning a text change into Y.Text splices (plugin spec §Bridge: "diff
// shadow text → apply as Y.Text splice(s) (diff-match-patch, char-level)").
// Char-level diffs merge best under Yjs — two peers editing different regions
// of the same paragraph both survive, which is what makes the no-conflict-
// files policy honest.

import DiffMatchPatch from "diff-match-patch";
import type * as Y from "yjs";

const dmp = new DiffMatchPatch();

/**
 * Apply the change from `oldText` to `newText` onto `ytext` as insert/delete
 * splices, inside the given transaction. `ytext`'s current string MUST equal
 * `oldText` (the shadow) for the indices to line up. No-op when unchanged.
 */
export function applyTextDiff(
  ytext: Y.Text,
  oldText: string,
  newText: string,
): void {
  if (oldText === newText) return;
  const diffs = dmp.diff_main(oldText, newText);
  // Char-level: do NOT run semantic cleanup, which coalesces into larger,
  // coarser edits that merge worse.
  let cursor = 0;
  for (const [op, text] of diffs) {
    if (op === DiffMatchPatch.DIFF_EQUAL) {
      cursor += text.length;
    } else if (op === DiffMatchPatch.DIFF_DELETE) {
      ytext.delete(cursor, text.length);
    } else {
      // DIFF_INSERT
      ytext.insert(cursor, text);
      cursor += text.length;
    }
  }
}

/** One run of a display diff: `op` is -1 (removed), 0 (unchanged), or 1
 *  (added); `text` is the run's characters. */
export interface DiffSegment {
  op: -1 | 0 | 1;
  text: string;
}

/**
 * Char-level diff of `oldText`→`newText` cleaned up to human-readable runs, for
 * SHOWING what changed (e.g. version history). This is the display counterpart
 * to `applyTextDiff` — here we DO run semantic cleanup so added/removed spans
 * read as words/phrases rather than scattered characters. Never used to mutate
 * a CRDT (that path stays char-level for better merges).
 */
export function diffSegments(oldText: string, newText: string): DiffSegment[] {
  const diffs = dmp.diff_main(oldText, newText);
  dmp.diff_cleanupSemantic(diffs);
  return diffs.map(([op, text]) => ({ op: op as -1 | 0 | 1, text }));
}

/**
 * Three-way merge of a local disk edit into a CRDT text that may have already
 * absorbed concurrent remote edits (plugin spec §Startup reconcile "both
 * changed" case). `base` is the last-synced (shadow) text; the CRDT's current
 * string is the merge target. We build the base→disk patch and fuzzily apply
 * it onto the current CRDT text (so remote edits survive), then splice the
 * CRDT text up to the merged result — transferring our edits into the CRDT
 * char-wise. Returns the merged text now in `ytext`.
 *
 * When the CRDT is unchanged (`base` === current), this degenerates to a
 * plain push: the result is exactly `diskText`.
 */
export function threeWayMergeInto(
  ytext: Y.Text,
  base: string,
  diskText: string,
): string {
  const crdtText = ytext.toString();
  if (base === crdtText) {
    // Fast path (pure local edit): apply the disk change directly.
    applyTextDiff(ytext, crdtText, diskText);
    return diskText;
  }
  const patches = dmp.patch_make(base, diskText);
  const [merged] = dmp.patch_apply(patches, crdtText) as [string, boolean[]];
  applyTextDiff(ytext, crdtText, merged);
  return merged;
}
